import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import { PinnedFieldsVisibility } from "@mapmycustomers/shared";
import RoleEnum from "@mapmycustomers/shared/enum/Role";
import Visibility from "@mapmycustomers/shared/enum/Visibility";
import Role from "@mapmycustomers/shared/types/Role";
import Team from "@mapmycustomers/shared/types/Team";

import { LabeledSwitch } from "@app/component/input/LabeledSwitch";
import VisibilitySelectField from "@app/component/input/VisibilitySelectField";
import { getRole } from "@app/store/iam";
import { getTeams } from "@app/store/members";
import EntityColumnsConfiguration from "@app/store/recordPreview/EntityColumnsConfiguration";
import { RootState } from "@app/store/rootReducer";

import messages from "../messages";
import getEngagementInfoVisibilityNote from "../util/getEngagementInfoVisibilityNote";
import getQuickActionsVisibilityNote from "../util/getQuickActionsVisibilityNote";
import useVisibilityOptions from "../util/useVisibilityOptions";

import styles from "./Details.module.scss";

const teamsRenderer = () => null;

interface Props {
  details: Pick<EntityColumnsConfiguration, "actions" | "engagement">;
  onChange: (details: Pick<EntityColumnsConfiguration, "actions" | "engagement">) => void;
  teams: Team[];
  userRole: Role;
}

export const Details: React.FC<Props> = ({ details, onChange, teams, userRole }) => {
  const intl = useIntl();
  const isOwner = userRole.key === RoleEnum.OWNER;

  const visibilityOptions = useVisibilityOptions(userRole.key);

  const quickActionsDisabled = useMemo(() => {
    return (
      !!details.actions.visibility && details.actions.visibility !== Visibility.PRIVATE && !isOwner
    );
  }, [details.actions.visibility, isOwner]);

  const engagementDisabled = useMemo(() => {
    return (
      !!details.engagement.visibility &&
      details.engagement.visibility !== Visibility.PRIVATE &&
      !isOwner
    );
  }, [details.engagement.visibility, isOwner]);

  const handleQuickActionsVisibilityChange = useCallback(
    (visibility) => {
      onChange({ ...details, actions: { ...details.actions, visibility } });
    },
    [details, onChange]
  );

  const handleQuickActionsTeamIdsChange = useCallback(
    (teamIds: Team["id"][]) => {
      onChange({ ...details, actions: { ...details.actions, teamIds } });
    },
    [details, onChange]
  );

  const handleEngagementTeamIdsChange = useCallback(
    (teamIds: Team["id"][]) => {
      onChange({ ...details, engagement: { ...details.engagement, teamIds } });
    },
    [details, onChange]
  );

  const handleEngagementVisibilityChange = useCallback(
    (visibility) => {
      onChange({ ...details, engagement: { ...details.engagement, visibility } });
    },
    [details, onChange]
  );

  const handleChangeQuickActions = useCallback(
    (checked: boolean) => {
      onChange({
        ...details,
        actions: { ...details.actions, visibility: checked ? Visibility.PRIVATE : undefined },
      });
    },
    [details, onChange]
  );

  const handleChangeEngagement = useCallback(
    (checked: boolean) => {
      onChange({
        ...details,
        engagement: { ...details.engagement, visibility: checked ? Visibility.PRIVATE : undefined },
      });
    },
    [details, onChange]
  );

  return (
    <div className={styles.container}>
      <div className={styles.visibilityRow}>
        <LabeledSwitch
          checked={!!details.actions.visibility}
          description={intl.formatMessage(messages.quickActionsInfo)}
          disabled={quickActionsDisabled}
          labelClassName={styles.switch}
          onChange={handleChangeQuickActions}
          title={intl.formatMessage(messages.quickActions)}
        />
        {!!details.actions.visibility && (
          <VisibilitySelectField<PinnedFieldsVisibility>
            className={styles.visibility}
            disabled={quickActionsDisabled || !isOwner}
            label={intl.formatMessage(messages.enableFor)}
            noteGetter={getQuickActionsVisibilityNote}
            onChange={handleQuickActionsVisibilityChange}
            onTeamIdsChange={handleQuickActionsTeamIdsChange}
            options={visibilityOptions}
            size="middle"
            teamIds={details.actions.teamIds}
            teams={teams}
            teamsRenderer={quickActionsDisabled ? teamsRenderer : undefined}
            value={details.actions.visibility}
          />
        )}
      </div>
      <div className={styles.visibilityRow}>
        <LabeledSwitch
          checked={!!details.engagement.visibility}
          description={intl.formatMessage(messages.engagementInfo)}
          disabled={engagementDisabled}
          labelClassName={styles.switch}
          onChange={handleChangeEngagement}
          title={intl.formatMessage(messages.engagement)}
        />
        {!!details.engagement.visibility && (
          <VisibilitySelectField<PinnedFieldsVisibility>
            className={styles.visibility}
            disabled={engagementDisabled || !isOwner}
            label={intl.formatMessage(messages.enableFor)}
            noteGetter={getEngagementInfoVisibilityNote}
            onChange={handleEngagementVisibilityChange}
            onTeamIdsChange={handleEngagementTeamIdsChange}
            options={visibilityOptions}
            size="middle"
            teamIds={details.engagement.teamIds}
            teams={teams}
            teamsRenderer={engagementDisabled ? teamsRenderer : undefined}
            value={details.engagement.visibility}
          />
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  teams: getTeams(state),
  userRole: getRole(state)!,
});

export default connect(mapStateToProps)(Details);
